import { sessionKeys } from '@taler/api-client'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'

export interface UseLedgerLifecycle {
  readonly isDisabling: ComputedRef<boolean>
  readonly disableError: ComputedRef<Error | null>
  disable(ledgerId: string): void
  readonly isRestoring: ComputedRef<boolean>
  readonly restoreError: ComputedRef<Error | null>
  restore(ledgerId: string): void
}

/** Both actions change the ledger's `archivedAt`, which lives on the ledgers list the session reads. */
export function useLedgerLifecycle(): UseLedgerLifecycle {
  const api = useApi()
  const queryClient = useQueryClient()

  const disableMutation = useMutation({
    mutationFn: (ledgerId: string) => api.session.disableLedger(ledgerId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: sessionKeys.ledgers() }),
  })

  const restoreMutation = useMutation({
    mutationFn: (ledgerId: string) => api.session.restoreLedger(ledgerId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: sessionKeys.ledgers() }),
  })

  return {
    isDisabling: computed(() => disableMutation.isPending.value),
    disableError: computed(() => disableMutation.error.value),
    disable: (ledgerId) => disableMutation.mutate(ledgerId),
    isRestoring: computed(() => restoreMutation.isPending.value),
    restoreError: computed(() => restoreMutation.error.value),
    restore: (ledgerId) => restoreMutation.mutate(ledgerId),
  }
}
